import { DestroyRef, inject, Injectable } from '@angular/core';
import { BehaviorSubject, EMPTY, forkJoin, from, merge, Observable, of, shareReplay } from 'rxjs';
import { filter, map, mergeMap, switchMap, take, tap, toArray } from 'rxjs/operators';
import { arrayDiff, arrayDiffIdCompare, fromArray, isUndefined, tapSubscribed } from '../utils';
import { FileCacheModel, ISpecial, ISpecialRaw } from '../models';
import { FileCacheService } from './file-cache.service';
import { WatchdogService } from './watchdog.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { WebsocketService } from './websocket.service';
import { SpecialsRepository } from '../repositories/specials.repository';
import { FileCacheRepository } from '../repositories/file-cache.repository';
import { hasOne } from '../database/repository.operators';
import { AuthService } from './auth.service';
import { ClusterService } from './cluster.service';

interface ToUpdate<T> {
  origin: T;
  value: T;
}

@Injectable()
export class SpecialsService {

  private readonly destroyRef = inject(DestroyRef);
  private readonly auth = inject(AuthService);
  private readonly cluster = inject(ClusterService);
  private readonly filesCache = inject(FileCacheService);
  private readonly specialsRepository = inject(SpecialsRepository);
  private readonly filesCacheRepository = inject(FileCacheRepository);
  private readonly watchdog = inject(WatchdogService);
  private readonly webSocket = inject(WebsocketService);

  private readonly logger = this.watchdog.tag('Specials', 'lime');
  public readonly loading$ = new BehaviorSubject<boolean>(true);

  public readonly specials$ = this.specialsRepository.all$().pipe(
    tapSubscribed(() => {
      this.loading$.next(true);
    }),
    hasOne(
      this.filesCacheRepository,
      'url',
      'content',
      'contentLocal'
    ),
    map((specials) => {
      if (specials === undefined) {
        return undefined;
      }

      return (Array.isArray(specials) ? specials : [specials])
        .filter(special => {
          const today = new Date();
          const start = new Date(special.scheduleStart);
          const end = new Date(special.scheduleEnd);
          return today >= start && today <= end;
        })
        .sort((a, b) => a.sequence - b.sequence)
        .map(special => {
          const contentFile = special.contentLocal
            ? new FileCacheModel(special.contentLocal)
            : undefined;

          return {
            ...special,
            contentLocal: contentFile
              ? contentFile.objectUrl
              : undefined,
            contentType: this.getContentType(special.mimeType),
          } satisfies ISpecial;
        })
    }),
    tap(() => this.loading$.next(false)),
    shareReplay(1),
  );

  public initialize(): void {
    this.cluster.leader$.pipe(
      filter((leader) => !!leader),
      switchMap(() => this.auth.logouted$),
      switchMap(() => this.clear()),
      tap(() => this.logger.info('Cleared specials on logout')),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe();

    this.webSocket.messages$.pipe(
      filter((response) => response.type === 'tableInfo'),
      switchMap((response) => {
        if (!response.data.specials) {
          return this.sync([]);
        }

        return this.sync(response.data.specials);
      }),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe();
  }

  public sync(specialsWS: ISpecialRaw[]): Observable<ISpecialRaw[]> {
    return this.filesCache.downloadFiles(specialsWS.map((special) => (
      {
        url: special.content,
        mimeType: special.mimeType,
      }
    ))).pipe(
      tapSubscribed(() => {
        this.logger.debug('Start sync special');
        this.loading$.next(true);
      }),
      switchMap(() => this.specialsRepository.all$().pipe(take(1))),
      switchMap((currentSpecials) => {
        const add = arrayDiff(specialsWS, currentSpecials, arrayDiffIdCompare);
        const del = arrayDiff(currentSpecials, specialsWS, arrayDiffIdCompare);
        const upd = currentSpecials.reduce<ToUpdate<ISpecialRaw>[]>((acc, origin) => {
          const value = specialsWS.find((d) => d.id === origin.id);
          if (value && JSON.stringify(value) !== JSON.stringify(origin)) {
            acc.push({
              origin,
              value,
            });
          }

          return acc;
        }, []);

        return merge(
          this.toAdd(add),
          this.toUpdate(upd),
          this.toDelete(del),
        );
      }),
      tap(() => this.loading$.next(false))
    );
  }

  private toAdd(specials: ISpecialRaw[]): Observable<ISpecialRaw[]> {
    if (specials.length === 0) {
      return of([]);
    }

    return from(specials).pipe(
      tap((special) => this.logger.debug('Special to add', special)),
      mergeMap((special) => {
        return this.filesCache.downloadFile(special.content, { mimeType: special.mimeType }).pipe(
          map(() => special),
        );
      }),
      mergeMap((special) => this.specialsRepository.add$(special)),
      toArray(),
      tap((special) => this.logger.debug('Special added', special)),
    );
  }

  private toUpdate(specials: ToUpdate<ISpecialRaw>[]): Observable<ISpecialRaw[]> {
    if (specials.length === 0) {
      return of([]);
    }

    return fromArray(specials).pipe(
      tap((special) => this.logger.debug('Special to update', special)),
      mergeMap(({ origin, value }) => {
        const download$: Observable<unknown> = value.content === origin.content
          ? of(null)
          : this.filesCache.downloadFile(value.content, { mimeType: value.mimeType });

        return download$.pipe(
          mergeMap(() => this.specialsRepository.update$(value).pipe(
            map(() => value),
          )),
          mergeMap((special) => {
            if (!special) {
              return EMPTY;
            }

            if (special.content !== origin.content) {
              return this.filesCacheRepository.delete$(origin.content).pipe(
                take(1),
                map(() => special),
              );
            }

            return of(special);
          }),
        );
      }),
      toArray(),
      tap((special) => this.logger.debug('Special updated', special)),
    );
  }

  private toDelete(specials: ISpecialRaw[]): Observable<ISpecialRaw[]> {
    if (specials.length === 0) {
      return of([]);
    }

    return fromArray(specials).pipe(
      tap((special) => this.logger.debug('Special to delete', special)),
      mergeMap((special) => {
        return this.specialsRepository.delete$(special.id).pipe(
          map(() => special),
        )
      }),
      mergeMap((special) => {
        if (!special) {
          return EMPTY;
        }

        return this.filesCacheRepository.delete$(special.content).pipe(
          take(1),
          map(() => special),
        );
      }),
      toArray(),
      tap((special) => this.logger.debug('Special deleted', special)),
    );
  }

  private clear(): Observable<string[] | null> {
    return this.specialsRepository.all$().pipe(
      take(1),
      switchMap((specials) => {
        if (isUndefined(specials)) {
          return of(specials);
        }

        const urls = specials.map((special) => {
          return special.content;
        }).flat();

        return forkJoin(urls.map((url) => {
          return this.filesCacheRepository.delete$(url).pipe(
            map(() => url),
          );
        }));
      }),
      switchMap((urls) => {
        return this.specialsRepository.clear$().pipe(
          map(() => urls),
        );
      })
    );
  }

  public getContentType(mimeType: string): 'image' | 'video' | undefined {

    if (mimeType.toLowerCase().startsWith('video')) {
      return 'video';
    } else if (mimeType.toLowerCase().startsWith('image')) {
      return 'image';
    }

    return undefined;
  }
}
